/**
 * Pricing utility for fetching and formatting subscription prices
 * All prices come from the backend database - no hardcoded values
 */

import { paymentAPI } from './api';

// Cache for subscription plans
let cachedPlans = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch subscription plans from the API
 * @returns {Promise<Object>} - { plans: [], currency: 'INR' }
 */
export const fetchSubscriptionPlans = async () => {
  try {
    // Check cache
    if (cachedPlans && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      return cachedPlans;
    }

    const response = await paymentAPI.getSubscriptionPlans();

    cachedPlans = {
      plans: response.plans || [],
      currency: response.currency || 'INR',
      totalPlans: response.total_plans || 0
    };
    cacheTimestamp = Date.now();

    return cachedPlans;
  } catch (error) {
    console.error('Error fetching subscription plans:', error);
    // Return fallback data
    return {
      plans: [],
      currency: 'INR',
      totalPlans: 0
    };
  }
};

/**
 * Get pricing for a specific tier
 * @param {Array} plans - Array of plan objects from API
 * @param {string} tier - 'starter', 'professional', 'enterprise', or 'free'
 * @returns {Object} - Plan pricing data
 */
export const getTierPricing = (plans, tier) => {
  const plan = plans.find(p => p.tier?.toLowerCase() === tier?.toLowerCase());

  if (!plan) {
    return {
      monthly: 0,
      yearly: 0,
      originalMonthly: 0,
      originalYearly: 0,
      discountPercent: 0,
      currency: 'INR'
    };
  }

  return {
    monthly: plan.price_monthly || 0,
    yearly: plan.price_yearly || 0,
    originalMonthly: plan.original_price_monthly || plan.price_monthly || 0,
    originalYearly: plan.original_price_yearly || plan.price_yearly || 0,
    discountPercent: plan.discount_percent || 0,
    currency: plan.currency || 'INR',
    features: plan.features || [],
    limits: plan.limits || {},
    name: plan.name || tier,
    isPopular: plan.is_popular || false
  };
};

/**
 * Format price with currency symbol
 * @param {number} amount - Price amount
 * @param {string} currency - Currency code (default: INR)
 */
export const formatPrice = (amount, currency = 'INR') => {
  if (amount === 0) return 'Free';

  const currencySymbols = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };

  const symbol = currencySymbols[currency] || currency + ' ';

  // Format number with locale
  if (currency === 'INR') {
    return `${symbol}${amount.toLocaleString('en-IN')}`;
  }

  return `${symbol}${amount.toLocaleString('en-US')}`;
};

/**
 * Calculate discount percentage
 * @param {number} original - Original price
 * @param {number} discounted - Discounted price
 * @returns {number} - Discount percentage
 */
export const calculateDiscount = (original, discounted) => {
  if (!original || original <= 0) return 0;
  return Math.round(((original - discounted) / original) * 100);
};

/**
 * Get time remaining for launch offer (7-day window from first visit)
 * Uses localStorage so the offer genuinely expires and doesn't reset.
 * @returns {Object} - { hours, minutes, seconds, expired }
 */
export const getDiscountTimeRemaining = () => {
  const OFFER_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
  const STORAGE_KEY = 'ct_offer_start';

  let startTime = localStorage.getItem(STORAGE_KEY);

  if (!startTime) {
    startTime = Date.now();
    localStorage.setItem(STORAGE_KEY, startTime.toString());
  } else {
    startTime = parseInt(startTime);
  }

  const elapsed = Date.now() - startTime;
  const remaining = Math.max(0, OFFER_DURATION - elapsed);

  if (remaining <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  const totalHours = Math.floor(remaining / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds, expired: false };
};

/**
 * Clear pricing cache (useful for testing)
 */
export const clearPricingCache = () => {
  cachedPlans = null;
  cacheTimestamp = null;
};

export default {
  fetchSubscriptionPlans,
  getTierPricing,
  formatPrice,
  calculateDiscount,
  getDiscountTimeRemaining,
  clearPricingCache
};
